// src/components/StaffVaccineReservationList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  VStack,
  HStack,
  Spinner,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faCircle } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import { tableCustomStyles, StatusBadge } from "../themes/TableStyles";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";
import CustomDatePicker, {convertToThDate} from "./CustomDatePicker";

// Configuration สำหรับแสดงสถานะด้วยสี
const statusConfig = {
  ใบจองใหม่: { color: "blue.500", label: "ใบจองใหม่" },
  รอการอนุมัติ: { color: "orange.500", label: "รอการอนุมัติ" },
  ยืนยันการจอง: { color: "green.500", label: "ยืนยันการจอง" },
  พร้อมรับวัคซีน: { color: "pink.500", label: "พร้อมรับวัคซีน" },
  รับวัคซีนแล้ว: { color: "purple.500", label: "รับวัคซีนแล้ว" },
  ยกเลิกการจอง: { color: "red.500", label: "ยกเลิกการจอง" },
  ปฏิเสธการจอง: { color: "red.600", label: "ปฏิเสธการจอง" },
  เกินกำหนดรับ: { color: "gray.500", label: "เกินกำหนดรับ" },
};

// Component สำหรับแสดงสถานะแบบ "จุดสี" พร้อมข้อความ
const StatusBadgeWithDot = ({ status }) => {
  const config = statusConfig[status] || { color: "gray.500" };

  return (
    <HStack spacing={2} align="center">
      <Box color={config.color} display="flex" alignItems="center">
        <FontAwesomeIcon icon={faCircle} size="xs" />
      </Box>
      <Text>{status}</Text>
    </HStack>
  );
};

// รายการสถานะทั้งหมด
const statusOptions = [
  "ใบจองใหม่",
  "รอการอนุมัติ",
  "ยืนยันการจอง",
  "พร้อมรับวัคซีน",
  "รับวัคซีนแล้ว",
  "ยกเลิกการจอง",
  //"ปฏิเสธการจอง",
  //"เกินกำหนดรับ",
];

const StaffVaccineReservationList = () => {
  const navigate = useNavigate();
  const { sessionId } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [deliveryPeriods, setDeliveryPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPickupPoint, setSelectedPickupPoint] = useState("");
  const [selectedDeliveryPeriod, setSelectedDeliveryPeriod] = useState("");

  const columns = [
    {
      name: "เลขที่ใบจอง",
      selector: (row) => row.ReservationID,
      sortable: true,
      width: "180px"
    },
    {
      name: "ชื่อเกษตรกร",
      selector: (row) => row.FarmerName,
      sortable: true,
    },
    {
      name: "รหัสเกษตรกร",
      selector: (row) => row.FarmerID || "-",
      sortable: true,
      width: "180px"
    },
    {
      name: "เบอร์โทร",
      selector: (row) => row.FarmerTel || "-",
      sortable: true,
      width: "150px"
    },
    {
      name: "รอบการจัดส่ง",
      selector: (row) => row.DeliveryPeriod || "-",
      sortable: true,
    },
    {
      name: "จุดจำหน่าย",
      selector: (row) => {
        const point = pickupPoints.find((p) => p.WarehouseID === row.PickupPoint);
        return point ? point.WarehouseName : row.PickupPoint || "-";
      },
      sortable: true,
    },
    {
      name: "วันที่จอง",
      selector: (row) => convertToThDate(row.ReservationDate),
      sortable: true,
      width: "180px"
    },
    {
      name: "สถานะ",
      cell: (row) => <StatusBadgeWithDot status={row.ReservationStatus} />,
      sortable: true,
      width: "200px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/staffvaccinereservationdetail/${row.ReservationID}`)}
          label="ดูรายละเอียด"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchDeliveryPeriods = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationPeriodList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const periods = response.DeliveryPeriods || [];
        // กรองเฉพาะรอบที่มี PeriodStart <= วันที่ปัจจุบัน
        const today = new Date();
        const validPeriods = periods.filter(period => {
          const periodStart = new Date(period.PeriodStart);
          return periodStart <= today;
        });
        setDeliveryPeriods(validPeriods);
      }
    } catch (error) {
      console.error("Error fetching delivery periods:", error);
    }
  };

  const fetchReservations = async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchPickupPoints(), fetchDeliveryPeriods()]);
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationsList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineReservationsList || [];
        setReservations(data);
        setFilteredReservations(data);
      }
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const handleSearchAndFilter = () => {
    let filtered = reservations;

    // กรองตามสถานะ
    if (selectedStatus) {
      filtered = filtered.filter((row) => row.ReservationStatus === selectedStatus);
    }

    // กรองตามจุดจำหน่าย
    if (selectedPickupPoint) {
      filtered = filtered.filter((row) => row.PickupPoint === selectedPickupPoint);
    }

    // กรองตามรอบการจัดส่ง
    if (selectedDeliveryPeriod) {
      filtered = filtered.filter((row) => row.DeliveryPeriod === selectedDeliveryPeriod);
    }

    // ค้นหาด้วยคำในช่องค้นหา
    if (searchTerm) {
      const searchTerms = searchTerm.toLowerCase().split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) => {
        const pickupPointName =
          pickupPoints.find((p) => p.WarehouseID === row.PickupPoint)?.WarehouseName ||
          row.PickupPoint ||
          "";
        return searchTerms.every((term) =>
          [
            row.ReservationID.toLowerCase(),
            row.FarmerName.toLowerCase(),
            row.FarmerID ? row.FarmerID.toLowerCase() : "",
            row.FarmerTel ? row.FarmerTel.toLowerCase() : "",
            row.DeliveryPeriod ? row.DeliveryPeriod.toLowerCase() : "",
            pickupPointName.toLowerCase(),
            row.ReservationStatus.toLowerCase(),
          ].some((field) => field.includes(term))
        );
      });
    }

    setFilteredReservations(filtered);
  };

  useEffect(() => {
    handleSearchAndFilter();
  }, [searchTerm, selectedStatus, selectedPickupPoint, selectedDeliveryPeriod, reservations]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handlePickupPointChange = (e) => {
    setSelectedPickupPoint(e.target.value);
  };

  const handleDeliveryPeriodChange = (e) => {
    setSelectedDeliveryPeriod(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSelectedStatus("");
    setSelectedPickupPoint("");
    setSelectedDeliveryPeriod("");
    setFilteredReservations(reservations);
  };

  const handleCreateNew = () => {
    navigate("/staffvaccinereservationdetail/new");
  };

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <StandardHeader
          title="รายการใบจองวัคซีน (สำหรับเจ้าหน้าที่)"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={{ base: 4, md: 6 }}
        >
          {reservations.length > 0 && (
            <>
              <InputGroup
                w={{ base: "100%", md: "30%", lg: "25%" }}
                mb={{ base: 4, md: 0 }}
              >
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faSearch} color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="ค้นหาใบจอง (เช่น RV2504007 Sutthipong)"
                  value={searchTerm}
                  onChange={handleSearch}
                  colorScheme="iBlue"
                  mt={1}
                />
              </InputGroup>
              <Select
                w={{ base: "100%", md: "25%", lg: "20%" }}
                placeholder="เลือกรอบการจัดส่ง"
                value={selectedDeliveryPeriod}
                onChange={handleDeliveryPeriodChange}
                colorScheme="iBlue"
                mt={{ base: 4, md: 1 }}
              >
                {deliveryPeriods.map((period) => (
                  <option key={period.DeliveryPeriod} value={period.DeliveryPeriod}>
                    {period.DeliveryPeriod}
                  </option>
                ))}
              </Select>
              <Select
                w={{ base: "100%", md: "25%", lg: "20%" }}
                placeholder="เลือกสถานะ"
                value={selectedStatus}
                onChange={handleStatusChange}
                colorScheme="iBlue"
                mt={{ base: 4, md: 1 }}
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Select>
              <Select
                w={{ base: "100%", md: "25%", lg: "20%" }}
                placeholder="เลือกจุดจำหน่าย"
                value={selectedPickupPoint}
                onChange={handlePickupPointChange}
                colorScheme="iBlue"
                mt={{ base: 4, md: 1 }}
              >
                {pickupPoints.map((point) => (
                  <option key={point.WarehouseID} value={point.WarehouseID}>
                    {point.WarehouseName}
                  </option>
                ))}
              </Select>
              <IconButton.Clear onClick={handleClearSearch} ml={{ base: 0, md: 5 }} />
            </>
          )}
          <IconButton.Add onClick={handleCreateNew} label="สร้างใบจองใหม่" />
        </HStack>
        {isLoading ? (
          <Spinner size="md" />
        ) : reservations.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลใบจองวัคซีน
          </Text>
        ) : filteredReservations.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredReservations}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default StaffVaccineReservationList;